/**
 * Every element a web tour step can point at.
 *
 * The API's `Tours/tour-manifest.json` lists the same ids under `anchors.web`, and
 * `tourAnchors.manifest.test.ts` fails when an id here is missing there. A step whose anchor is not
 * in this list is dropped client-side (see `renderableSteps`), so a step authored for a control
 * this build does not have never points at nothing.
 *
 * Tag an element by spreading `tourAnchor(id)` onto it. In a list, tag the first row only: the
 * overlay spotlights the first visible match.
 */
export const TOUR_ANCHORS = {
  web: [
    // calls
    "calls.title",
    "calls.export",
    "calls.schedule",
    "calls.filters",
    "calls.list",
    "calls.row.open",
    "calls.row.dial",
    "calls.row.notes",
    "calls.row.complete",
    "calls.row.reschedule",
    "calls.row.open-player",
    // players
    "players.list",
    "players.teams",
    // On the page: the + button on the team chip row, which opens the Add team modal.
    "players.teams.add",
    // On the page: the Manage Teams button in the players toolbar.
    "players.teams.manage",
    // Inside the Manage teams slide panel. The panel stays mounted while closed, so the
    // element exists either way; closed it is aria-hidden and inert, which is what makes the
    // engine treat it as unreachable and fall back to a centred card.
    "players.teams.panel",
    // Inside the Manage teams slide panel: its close button, so a tour can hand the page back.
    "players.teams.panel.close",
    "players.search",
    "players.filter",
    // Inside the Filter Prospects modal: its Customize Tags button.
    "players.filter.manage-tags",
    // Inside the Filter Prospects modal: the scrolling body that holds every filter section —
    // Grad Year, Level, JC Year and the org's own tag categories — rather than any one of them,
    // since which sections render depends on the org's data and its tag definitions.
    "players.filter.options",
    // Inside the Filter Prospects modal: the Apply button in its footer, which commits the
    // selection and closes the modal.
    "players.filter.apply",
    // Inside the Filter Prospects modal: its close button, so Back can take the coach out of the
    // modal to the Filter step that opened it.
    "players.filter.close",
    // Inside the Customize Tags modal: the modal body, then the New Category control. The
    // control is the "+ Add New Category" button until it is pressed and the New Category form
    // that replaces it afterwards, so the anchor is on both branches and only one is ever live.
    "players.tags.customize",
    "players.tags.category",
    // Inside the Customize Tags modal: its close button, so a tour can hand the page back.
    "players.tags.close",
    "players.columns",
    // Inside the column settings popover: the tick list, and the first row's drag handle.
    "players.columns.visibility",
    "players.columns.reorder",
    "players.upload",
    "players.add",
    "players.row.open",
    "players.row.favorite",
    "players.row.note",
    "players.row.video",
    "players.row.tag",
    "players.row.select",
    // rosters
    // Roster boards list (/rosters): the "+ New roster" header button, and the whole list area
    // (both sections, or the empty-state block when there are no boards).
    "rosters.index.new",
    "rosters.index.list",
    "rosters.title",
    "rosters.tabs",
    // On the page: the Depth chart tab in the roster's section nav.
    "rosters.depth-chart",
    "rosters.limits",
    // Inside the Roster settings modal: the scholarship model cards, the budget field (either
    // model's), roster spots, the position needs table (Dollar Budget only), the optional lists
    // (evaluation tags first, with indicators and pitching below), Save, and its close button.
    "rosters.configure.model",
    "rosters.configure.budget",
    "rosters.configure.spots",
    "rosters.configure.positions",
    "rosters.configure.optional",
    "rosters.configure.save",
    "rosters.configure.close",
    // On the page: the Scholarship & Roster Summary band above the board. It disappears when
    // the coach turns every summary card off, so a step on it is skipped rather than lost.
    "rosters.scholarship-total",
    "rosters.attach",
    // Inside the Attach players modal: the team search group, the select-all row above the
    // results, the Attach button, and the header Close button.
    "rosters.attach.team-search",
    "rosters.attach.select-all",
    "rosters.attach.submit",
    "rosters.attach.close",
    // The attach modal's "Add player", for a player who is not in RosterPilot yet.
    "rosters.attach.add-player",
    // The board's Upload roster button: imports a roster and attaches everyone on it.
    "rosters.upload",
    "rosters.add-players",
    "rosters.export",
    "rosters.row.open",
    "rosters.row.status",
    "rosters.row.edit",
    // Inside the player Edit modal: Award, Cost, the Financials & details section, the Pitching
    // section (pitchers only) and its Edit lists toggle, Save, and Cancel (which closes it).
    "rosters.player-edit.award",
    "rosters.player-edit.cost",
    "rosters.player-edit.finances",
    "rosters.player-edit.pitching",
    "rosters.player-edit.lists",
    "rosters.player-edit.save",
    "rosters.player-edit.close",
    "rosters.row.remove",
    // The first row's More actions button (three dots), which holds Add to other rosters.
    "rosters.row.more",
    "rosters.row.drag",
    // On the page: the Pitchers and Lineups tabs in the roster's section nav.
    "rosters.pitching-tab",
    "rosters.lineups-tab",
    // Depth chart tab: the first draggable row's handle, the colour legend under the field, and
    // the Field/List layout toggle.
    "rosters.depth.drag",
    // The first handle on a card's secondary-position rows, which sort as their own group.
    "rosters.depth.secondaryDrag",
    "rosters.depth.legend",
    "rosters.depth.layout",
    // Pitchers tab: first handle, first role select, first Edit pencil, the pitch type totals strip.
    "rosters.pitching.drag",
    "rosters.pitching.role",
    "rosters.pitching.edit",
    "rosters.pitching.totals",
    // Lineups tab: each side's card, the first handle, the first bench toggle, and the first
    // card's Fill from depth chart button (shown only while that lineup is empty).
    "rosters.lineups.lhp",
    "rosters.lineups.rhp",
    "rosters.lineups.drag",
    "rosters.lineups.bench",
    "rosters.lineups.fill",
    // notes
    "notes.title",
    "notes.new",
    "notes.list",
    "notes.more",
    "notes.row.open",
    // duplicates
    "duplicates.title",
    "duplicates.tabs",
    "duplicates.list",
    "duplicates.bulk",
    "duplicates.row.select",
    "duplicates.row.dismiss",
    "duplicates.row.merge",
    // schedule
    "schedule.calendar",
    "schedule.new",
    "schedule.find-itinerary",
    // Find events: the Schedule's way to the Events finder.
    "schedule.find-events",
    // One event's games laid out by day, opened from a multi-day event on the calendar.
    "schedule.event-calendar",
    "schedule.event-calendar.filter",
    "schedule.scope",
    "schedule.scope.staff-choice",
    // Month, Week, 3 days or Day, and the button that saves the one on screen as the default.
    "schedule.view",
    "schedule.view.default",
    // The staff picker beside the scope toggle: view another coach's schedule.
    "schedule.scope.staff",
    // The event's games list: select games and add or remove coaches in one go.
    "schedule.games.bulk-assign",
    "schedule.print",
    "schedule.updates",
    "schedule.row.open",
    "schedule.row.needs-setup",
    "schedule.row.unassigned",
    "schedule.catalog.list",
    "schedule.catalog.search",
    "schedule.catalog.row.add",
    "schedule.catalog.request",
    // tournament-finder (/tournaments). Add and Share sit on the first result card: a tour runs on
    // one route, so the detail page's own Add and Share buttons cannot be reached by this tour.
    "tournaments.search",
    "tournaments.filters",
    "tournaments.result",
    "tournaments.add",
    "tournaments.share",
    "tournaments.actions",
    // "Can't find it? Request this event" when a search finds nothing.
    "tournaments.request",
    // tournament-finder detail (/tournaments/:id), behind `directory.rosters`: the Teams section,
    // the first team's roster button, and Import inside the roster sheet.
    "tournaments.teams",
    "tournaments.teams.division",
    "tournaments.teamRoster",
    "tournaments.importRoster",
    // The Games section on the same detail page, behind `directory.tournaments` like the page.
    "tournaments.games",
    // The detail's three tabs, and the first game's drill-in (its two teams and their rosters).
    "tournaments.gamesTab",
    "tournaments.teamsTab",
    "tournaments.venuesTab",
    "tournaments.gameOpen",
    // player-database (/player-database), behind `directory.players`. Profile and Add sit on the
    // first result row.
    "directory.players.search",
    // "Top players at each level" on All levels before the list is narrowed (2026-10-30), rankings plans only.
    "directory.players.top-levels",
    "directory.players.filters",
    "directory.players.profile",
    "directory.players.import",
    // The results' Select all checkbox, which starts a bulk add (2026-10-23).
    "directory.players.select",
    // Tag without adding (2026-10-24): the first row's Tag button and the My tags filter.
    "directory.players.row-menu",
    "directory.players.my-tags",
    // The profile's Add dialog: its Tags and Recruiting boards pickers and its Add button.
    "directory.players.add.tags",
    "directory.players.add.boards",
    "directory.players.add.submit",
    // Epic 6: More Filters (stat filters), the first sortable stat header, and the Columns menu.
    "directory.players.statFilters",
    "directory.players.stats",
    // Stat filters v1 (2026-11-03): the Stats section of More filters (All Players and the Transfer Portal):
    // the Hitting / Pitching switch, the core stat rows, the first row's Top %, the PA / IP minimum,
    // More stat filters, the presets row and Save search / Saved searches. Flow step tour
    // `player-database.stat-filters`.
    "directory.players.stats.role",
    "directory.players.stats.core",
    "directory.players.stats.percentile",
    "directory.players.stats.minimum",
    "directory.players.stats.more",
    "directory.players.stats.presets",
    "directory.players.stats.saved",
    "directory.players.columns",
    "directory.players.levels",
    "directory.players.sort",
    // The ranking headers (2026-10-22, the rankings moved into this table): Rank on the college
    // levels and All, National rank on HS. A plan without rankings has neither.
    "directory.players.rank",
    "directory.players.hs-rank",
    // The Worth to you header (the estimate adjusted for the viewer's program level), college programs only.
    "directory.players.worth-to-you",
    // Fit Rank (replaced Program Rank, 2026-11-02): the level tabs' Fit Rank header (college levels, with rankings).
    "directory.players.fit-rank",
    // Fit Rank's state in the needs strip (the status pill, or the "needs your school" prompt), and Quick setup's
    // button in the banner (portal and level tabs).
    "directory.players.fit-rank.status",
    "directory.players.fit-rank.setup",
    // The Worth to you settings button beside Columns (budget level), college programs only.
    "directory.players.worth-settings",
    // The Player type chips (Pitchers, Position players, All) in the filter bar.
    "directory.players.role",
    // Patrick's JUCO and D1 screens (2026-10-28): the pills under the level tabs (JUCO divisions, D1
    // portal status), D1's Year chips, and the National rank and Portal headers.
    "directory.players.division",
    "directory.players.year",
    "directory.players.national-rank",
    "directory.players.portal-status",
    // The first result row's school button, which opens that school's roster (high school too).
    "directory.players.school",
    // Epic 8: the Transfer Portal tab, its filters, and the Portal Score column header. The
    // redesign adds the division tabs, the School header (sorts), the Columns menu and the first
    // entry's name (opens the entry).
    "directory.players.portal.tab",
    "directory.players.portal.filters",
    "directory.players.portal.score",
    "directory.players.portal.divisions",
    "directory.players.portal.sort",
    "directory.players.portal.columns",
    // The first stat column header on screen (AVG by default).
    "directory.players.portal.stats",
    "directory.players.portal.entry",
    "directory.players.portal.add",
    // The first entry's school button, which opens that school's roster.
    "directory.players.portal.school",
    // College rankings v1: the Rank and Est. value headers (Est. value is shown to college programs only).
    "directory.players.portal.rank",
    "directory.players.portal.value",
    // Fit Rank (2026-11-02): the portal's Fit Rank header, the default order.
    "directory.players.portal.fit-rank",
    // Worth to you beside it: the estimate adjusted for the viewer's program level (college programs only).
    "directory.players.portal.worth-to-you",
    // The Committed to header (2026-10-23): where a player went, read from later rosters.
    "directory.players.portal.committed",
    // The portal's Player type chips (All, Pitchers, Position players), which pick the ranking the rank reads.
    "directory.players.portal.role",
    // Tagging on the portal (2026-10-27): its My tags filter, the Select every matched player box and the
    // first linked row's More actions button (Tag or Edit tags).
    "directory.players.portal.my-tags",
    "directory.players.portal.select",
    "directory.players.portal.row-menu",
    // The Schools tab: its tab, the level chips, the search box, the first school's name (opens its
    // roster), and inside the roster sheet its summary and close button.
    "directory.players.schools.tab",
    "directory.players.schools.levels",
    "directory.players.schools.search",
    "directory.players.schools.row",
    "directory.players.schools.roster",
    "directory.players.schools.roster.close",
    // College rankings v1: the roster sheet's estimated roster value (college programs only).
    "directory.players.schools.roster.value",
    // College rankings v1: the College ranking block on the player profile.
    "directory.players.profile.ranking",
    // The profile sheet's own flow step tour (`player-database.profile`).
    "directory.players.profile.ranks",
    // Nov 4 C3: a ranked role's WAR breakdown on the profile's College ranking block.
    "directory.players.profile.war-breakdown",
    "directory.players.profile.add",
    // The Contact plan on the Player Database profile, under Add or Tag (works before he is added).
    "directory.players.profile.contact-plan",
    "directory.players.profile.cards",
    "directory.players.profile.production",
    "directory.players.profile.tabs",
    // The Comparable players card (not drawn for a high school player).
    "directory.players.profile.comparables",
    // Roster planning on the Player Database (`player-database.planning`): the Your needs card's
    // Roster planning button, the Roster planning tab, the tray's board picker, gear, totals and
    // positions, Put on board in the selection bar, and the first board row's Count box.
    "directory.players.needs.expand",
    "directory.players.plan.tab",
    "directory.players.plan.board",
    "directory.players.plan.setup",
    "directory.players.plan.bulk",
    "directory.players.plan.totals",
    "directory.players.plan.positions",
    "directory.players.plan.count",
    // The "Your needs" card above All Players (roster count and the planning board's needs).
    "directory.players.needs",
    // AI player search (directory.ai_search): the box above All Players, then in the conversation the
    // filter chips, the live panel beside the chat and the follow-ups under the newest answer.
    "directory.players.ai.box",
    "directory.players.ai.chips",
    "directory.players.ai.panel",
    "directory.players.ai.suggestions",
    // The app-wide assistant (ai.assistant): the header's Ask button on every page, then in the open panel the
    // panel itself, its composer and its Attach a file button, the view column (only once an answer shows something)
    // and the follow-ups.
    "assistant.open",
    "assistant.panel",
    "assistant.composer",
    "assistant.attach",
    "assistant.view",
    "assistant.suggestions",
    // The Support page (/support), open to every user: its tab list. Then, on the Contact support
    // tab (behind `support.helpdesk`), the New request button and the list of the coach's own
    // requests (tagged even when empty), and the Browse help articles link above New request.
    "support.tabs",
    "support.newTicket",
    "support.list",
    "support.helpArticles",
    // The Help Center item in the left nav, beside Logout. On every page; the Players tour points
    // at it so coaches know where tours, Get Started and support live.
    "nav.helpCenter",
    // The Home page (/home): the Needs your attention card, its first row's action button (first
    // row only), the Today's schedule card and the Transfer portal card. Then the Home item in the
    // left nav.
    "home.attention",
    "home.attention.row",
    "home.schedule",
    "home.portal",
    "nav.home",
    // The "In directory" section on a prospect's profile, inside the player viewer on /players.
    "prospect.directory.link",
    // Its Stats block, shown only once the prospect is matched (season tiles and season tables).
    "prospect.directory.stats",
    // The Saved links section on a prospect's profile (staff-saved video and social links).
    "prospect.saved-links",
    // The Schedule call button in the player profile header, inside the player viewer on /players.
    // Shown only with the calls Page Access key.
    "players.profile.schedule-call",
    // The Contact plan (priority and check-in cadence) under the player profile header, beside Schedule call.
    "players.profile.contact-plan",
    // The Recruiting info block (travel team, high school team) on a prospect's About tab.
    "players.profile.recruiting-info",
    // video
    "video.grid",
    "video.search",
    "video.filters",
    "video.tags",
    "video.row.open",
    "video.row.clip",
    // roster-import
    "roster-import.type.options",
    "roster-import.type.juco",
    "roster-import.type.continue",
    // The "Recruiting info" upload type, and its review step (the match table and Save).
    "roster-import.type.recruiting-info",
    "roster-import.recruiting-info.table",
    "roster-import.recruiting-info.save",
    "roster-import.upload.file",
    "roster-import.mapping.columns",
    "roster-import.mapping.preview",
    "roster-import.mapping.reextract",
    "roster-import.team.search",
    "roster-import.team.follow",
    "roster-import.team.submit",
    "roster-import.review.names",
    "roster-import.review.juco",
    "roster-import.review.import",
    // travel-ball-import
    "travel-ball-import.file",
    "travel-ball-import.upload",
    "travel-ball-import.review",
    "travel-ball-import.save",
    // travel-ball-admin-players
    "travel-ball-admin-players.filters",
    "travel-ball-admin-players.table",
    "travel-ball-admin-players.row",
    // build-itinerary
    "build-itinerary.event.source",
    "build-itinerary.event.name",
    "build-itinerary.match.summary",
    "build-itinerary.match.list",
    "build-itinerary.follow.list",
    "build-itinerary.follow.next",
    "build-itinerary.follow.add-player",
    "build-itinerary.crawled.teams",
    "build-itinerary.pitchers.list",
    "build-itinerary.pitchers.next",
    // Type a pitcher the coach does not follow, on a game.
    "build-itinerary.pitchers.type",
    "build-itinerary.assign.auto",
    "build-itinerary.assign.to",
    "build-itinerary.assign.quick",
    "build-itinerary.assign.pool",
    "build-itinerary.assign.finish",
    // pitch-chart (/pitch-charts/:id): the summary tables and the at-bats list.
    "pitchChart.summary",
    "pitchChart.atBats",
  ],
} as const;

export type TourAnchorId = (typeof TOUR_ANCHORS.web)[number];

export const WEB_TOUR_ANCHORS: ReadonlySet<string> = new Set(TOUR_ANCHORS.web);

export const TOUR_ANCHOR_ATTRIBUTE = "data-tour-anchor";

/**
 * The only controls the tour may press on a coach's behalf: ones that open a popup, a panel or a
 * menu, or close one the tour itself opened.
 *
 * The anchor vocabulary also names destructive controls (`duplicates.row.dismiss`,
 * `rosters.row.remove`), and steps are authored in the API's manifest rather than here. Without an
 * allowlist, a manifest edit — a typo, or a well-meaning step written against the wrong anchor —
 * would be enough to make a browser press one of those for a coach who only came to read a tour.
 * Anything else falls back to "point at it and press Next", which loses nothing but a convenience.
 *
 * It lives here, beside the vocabulary it draws from, because three unrelated places ask it:
 * `useStepInteraction` (may this step press?), `TourOverlay` (is this step interactive at all?) and
 * the end-of-run tidy-up in `tourDom` (may the tour close this again?).
 */
export const AUTO_PRESS_ANCHORS: ReadonlySet<string> = new Set<TourAnchorId>([
  "players.columns",
  // Opens the roster upload modal, for "Upload Your Roster" (`roster-import.start`).
  "players.upload",
  "players.filter",
  "players.teams.manage",
  "players.filter.manage-tags",
  "players.teams.panel.close",
  "players.tags.close",
  "players.filter.close",
  // Schools tab: the first school opens its roster sheet (read only), and its close button shuts it.
  "directory.players.schools.row",
  "directory.players.schools.roster.close",
  // Roster board: the three buttons that open the modals the board tour walks through. The Edit
  // pencil opens the first row's editor only; it never saves or removes anything. The modals' close
  // buttons are deliberately absent: each modal holds the coach's input (selected players, roster
  // settings, a player's money), so nothing here closes one. Each modal's last step asks the coach
  // to press Attach or Save themselves; Next on that step is the one exception, and goes through
  // `COMMIT_CLOSE_ANCHORS` below.
  "rosters.attach",
  "rosters.limits",
  "rosters.row.edit",
]);

/**
 * The commit button of each modal the board tour walks through (Attach, Save), paired with that
 * modal's own close button.
 *
 * On a commit step the page is live, so the coach can finish the form. Their press only *arms* the
 * step: it advances once the commit has actually finished, which is when the commit button leaves
 * the page (the modal closed). A save that fails validation, or is still in flight, keeps the coach
 * on the step with the form in front of them.
 *
 * The tour never presses a commit button, because these save data. Next on a commit step whose
 * modal is still open presses the paired close button instead (`closeCommitModal` in `tourDom`), so
 * the coach moves on without saving and the next modal never opens on top of this one. The same
 * close buttons are also pressed by `closeModalOpenedBy` (`MODAL_CLOSE_ANCHORS`), but only on Back
 * to the opener step or earlier and when another run is about to take the screen; a plain Skip or
 * Finish leaves the modal, and the coach's input, alone. Neither path presses a disabled close
 * button (a save in flight). They are not in `POPUP_CLOSE_ANCHORS`, and never put either side in
 * `AUTO_PRESS_ANCHORS`.
 */
export const COMMIT_CLOSE_ANCHORS: ReadonlyMap<string, string> = new Map<TourAnchorId, TourAnchorId>([
  ["rosters.attach.submit", "rosters.attach.close"],
  ["rosters.configure.save", "rosters.configure.close"],
  ["rosters.player-edit.save", "rosters.player-edit.close"],
]);

/** The commit buttons of `COMMIT_CLOSE_ANCHORS`: controls a step may name that the tour never presses. */
export const COACH_PRESS_ANCHORS: ReadonlySet<string> = new Set(COMMIT_CLOSE_ANCHORS.keys());

/**
 * Openers that only ever open their modal (`aria-haspopup="dialog"`): a second press never closes
 * it. They publish `aria-expanded` so Back can tell the modal is open and put it back, but the
 * end-of-run tidy-up must not read that flag as "press me to close".
 */
export const MODAL_OPENER_ANCHORS: ReadonlySet<string> = new Set<TourAnchorId>([
  "rosters.attach",
  "rosters.limits",
  "rosters.row.edit",
]);

/**
 * Each modal opener's own close button: the modal's Cancel or ×, never its Save or Attach.
 *
 * A modal opener cannot close what it opened, so when the run has to put the page back (Back to
 * the opener step or before it, or the run ending with the modal still up) it closes the modal
 * through this button instead. Pressing it discards whatever the coach typed in the modal and
 * saves nothing, which is what leaving the modal without pressing Save means anyway.
 */
export const MODAL_CLOSE_ANCHORS: ReadonlyMap<string, string> = new Map<TourAnchorId, TourAnchorId>([
  ["rosters.attach", "rosters.attach.close"],
  ["rosters.limits", "rosters.configure.close"],
  ["rosters.row.edit", "rosters.player-edit.close"],
]);

/**
 * Whether the tour may press this anchor for the coach.
 *
 * Asked by the overlay as well, which is the point: a step the allowlist refuses is not pressed,
 * so it must not be treated as interactive either. Without that, the shield comes off the page and
 * a destructive control sits spotlighted and live in a hole in the shade.
 */
export const canAutoPressAnchor = (anchorId: string): boolean => AUTO_PRESS_ANCHORS.has(anchorId);

/**
 * The close button of the popup each opener shows, for the popups whose opener says nothing about
 * being open (no `aria-expanded`), so pressing the opener again would not close them.
 *
 * The tour closes one of these for the coach when it moves on to a step that is not inside that
 * popup, and when the run ends. That is what lets the manifest go straight from the last step
 * inside the tag manager to "Choose your columns" without a "Close it when you are done" step
 * between them. Only a popup this run opened is closed: the opener's step has to be in the run's
 * record of presses. Both sides must also be in `AUTO_PRESS_ANCHORS`.
 */
export const POPUP_CLOSE_ANCHORS: ReadonlyMap<string, string> = new Map<TourAnchorId, TourAnchorId>([
  ["players.filter.manage-tags", "players.tags.close"],
  ["players.filter", "players.filter.close"],
  ["players.teams.manage", "players.teams.panel.close"],
  ["directory.players.schools.row", "directory.players.schools.roster.close"],
]);

/**
 * Anchors that only exist when the page has the record they point at: the first video card, and
 * the first clip's badge. A page tour's step on one of these is left out of the run when the anchor
 * is not on the page as the run starts (`withoutAbsentRecordSteps`), rather than shown as a
 * centered card about a video the coach does not have. Mobile keeps the same list in
 * `constants/tour-anchors.ts`.
 */
export const SKIP_WHEN_ABSENT_ANCHORS: ReadonlySet<string> = new Set<TourAnchorId>([
  // Find events and Request this event show only to a coach who can open the Events page, and the
  // request only after a search finds nothing; Type a pitcher needs a game.
  "schedule.find-events",
  "tournaments.request",
  "build-itinerary.pitchers.type",
  "video.row.open",
  "video.row.clip",
  // Stat filters: the presets row shows only when the catalog has presets for the role on screen, and
  // More stat filters only when the role has stats beyond the core ones.
  "directory.players.stats.presets",
  "directory.players.stats.more",
  // The one-tap coach buttons on an Assign row show only for five coaches or fewer.
  "build-itinerary.assign.quick",
  // The ranking headers are drawn only on their levels (Rank on college and All, National rank on
  // HS) and only for a plan with rankings, so the step for the other one is left out.
  "directory.players.rank",
  "directory.players.hs-rank",
  // The JUCO and D1 pills, D1's Year chips and the Portal header live on those tabs only, and National rank
  // needs rankings (and Columns can hide it and Portal).
  "directory.players.division",
  "directory.players.year",
  "directory.players.national-rank",
  "directory.players.portal-status",
  // College rankings v1: the estimated value is drawn for college programs only.
  "directory.players.portal.value",
  "directory.players.portal.worth-to-you",
  "directory.players.schools.roster.value",
  // Worth to you, like Est. value, is drawn for college programs only (and Columns can hide it).
  "directory.players.worth-to-you",
  // The Worth to you settings button shows only when the list carries values (college programs).
  "directory.players.worth-settings",
  // The portal's Rank column is the rankings plan feature's: a plan without it has no column to point at.
  "directory.players.portal.rank",
  // Fit Rank is the rankings plan feature's like Rank, and Columns can hide it, on the portal and the level tabs.
  "directory.players.portal.fit-rank",
  "directory.players.fit-rank",
  // The Top players grid shows only on All levels with nothing narrowing it, and only for a plan with rankings.
  "directory.players.top-levels",
  // Fit Rank's state shows only on a page with Fit Rank (the pill, or the prompt when the school is missing), and
  // the setup button only while the needs board has no needs, for a coach who can plan.
  "directory.players.fit-rank.status",
  "directory.players.fit-rank.setup",
  // A coach can hide Committed to with Columns.
  "directory.players.portal.committed",
  // Portal Score is in Columns, off by default since the client's Portal Table Redesign (2026-10-26),
  // and the stats step points at Key stats or the first stat a coach shows, either of which Columns hides.
  "directory.players.portal.score",
  "directory.players.portal.stats",
  // The Your needs card is drawn on All Players only, for a coach who can plan a roster, and so is its
  // Roster planning button (not on the Roster planning tab either).
  "directory.players.needs",
  "directory.players.needs.expand",
  // The AI search box shows only with the directory.ai_search flag; its conversation's anchors only
  // once a conversation has an answer.
  "directory.players.ai.box",
  "directory.players.ai.chips",
  "directory.players.ai.panel",
  "directory.players.ai.suggestions",
  // The assistant's view column and follow-ups exist only once an answer has shown something.
  "assistant.view",
  "assistant.suggestions",
  // My tags is drawn only for a program with tags set up.
  "directory.players.my-tags",
  "directory.players.portal.my-tags",
  // A portal page may have no entry matched to a player, and then no row has a tag action.
  "directory.players.portal.row-menu",
  // School rosters need the Player Database: a plan with only the portal shows the school as text.
  "directory.players.portal.school",
  // The Comparable players card is not drawn for a high school player.
  "directory.players.profile.comparables",
  // The Contact plan is not drawn against an API without contact plans (web deploys first).
  "players.profile.contact-plan",
  "directory.players.profile.contact-plan",
  // The profile's Ranks region is drawn only when he has a rank to show (Fit Rank, national or state).
  "directory.players.profile.ranks",
  // The WAR breakdown is drawn only under a ranked role with a WAR (Nov 4 C3).
  "directory.players.profile.war-breakdown",
]);

/**
 * Anchors whose step is left out of a run when every element carrying them is inside a closed panel (`hidden`), as
 * the run starts. On a phone the Player Database puts its level strip, the JUCO and D1 pills and Player type in the
 * closed Filters panel (Jordan, 2026-10-30); the "More filters" step on the Filters button says they are there. On a
 * wider screen they are on the page and their steps run as before.
 */
export const SKIP_WHEN_FOLDED_ANCHORS: ReadonlySet<string> = new Set<TourAnchorId>([
  "directory.players.levels",
  "directory.players.division",
  "directory.players.role",
]);

export const tourAnchor = (id: TourAnchorId): { "data-tour-anchor": TourAnchorId } => ({
  "data-tour-anchor": id,
});
